/**
 * RunClient — verified "Dams to AGI" games from the browser side.
 *
 * An arcade game (stage, lives, energy) lives on the server: startGame()
 * starts or resumes one (free once a day, else a credit), continueGame()
 * spends a credit when the lives run out. start() gets a server seed for the
 * game's current stage, the scene records inputs as [ms since stage start,
 * key], and finish() submits them for the server to replay with the shared
 * StageSim. Only server-verified stages count toward the prize pot.
 */

import XAuth from "./XAuth";

async function request(url, { method = "POST", body } = {}) {
  try {
    const resp = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json", ...XAuth.getAuthHeaders() },
      ...(method === "POST" ? { body: JSON.stringify(body ?? {}) } : {}),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      const { error = "Request failed", ...rest } = data;
      return { ok: false, status: resp.status, error, ...rest };
    }
    return { ok: true, ...data };
  } catch {
    return { ok: false, status: 0, error: "offline" };
  }
}

const RunClient = {
  /** @returns {Promise<{ ok: true, game: object|null, credits: number, freeGameAvailable: boolean } | { ok: false }>} */
  getGame() {
    return request("/api/game", { method: "GET" });
  },

  /**
   * Start or resume the arcade game; 402 INSERT_COIN when it needs a credit.
   * @param {number} [stage] - tester (comp) accounts only: a new game on this stage
   */
  startGame(stage) {
    return request("/api/game/start", { body: stage ? { stage } : {} });
  },

  /** Spend a credit to refill the lives; 402 INSERT_COIN without one. */
  continueGame() {
    return request("/api/game/continue");
  },

  /**
   * Start the game's current stage.
   * @returns {Promise<{ ok: true, runId: string, seed: number, level: number, startBankWh: number, startLives: number } | { ok: false, status: number, error: string, code?: string }>}
   */
  async start() {
    const res = await request("/api/run/start");
    if (!res.ok) return res;
    return {
      ok: true,
      runId: res.runId,
      seed: res.seed,
      level: res.level,
      startBankWh: res.startBankWh || 0,
      startLives: res.startLives,
    };
  },

  /** Submit the input log; the server's replay decides the outcome. */
  finish(runId, inputs) {
    return request("/api/run/finish", { body: { runId, inputs } });
  },
};

export default RunClient;
